// @story HIR-HD1
// ShortlistReview — the Head compares the candidates HR has put forward for a
// role, side by side, and records approve / hold / reject with an optional
// note. HR uses the same screen to put candidates forward.
// Data: GET /api/shortlist-review, GET /api/shortlist-review/:jobId,
// POST …/candidates/:id/decision | withdraw, POST …/put-forward (several at once).
//
// Laid out as the other EquaOS apps are (owner, 29 Oct 2026: "make it look
// exactly like the other systems"): a header, a row of blocks that filter what
// is below them, then the roles as one table (a row opens that role) or, for
// one role, the candidates side by side.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { Button, Card, EmptyState, Icon, Input, StatusPill, Table, useShellLayout } from '../../ds';
import { Page, PageHeader, StatRow, Stat, Section, Loading, Note } from '../../components/page';
import TopThree from '../rounds/TopThree';

const REC_WORDS = {
  strong_hire: 'Strong hire', hire: 'Hire', borderline: 'Borderline', no_hire: 'Do not hire',
  strong_no_hire: 'Strongly do not hire', time_expired: 'Ran out of time', no_response: 'No response',
};
const PANEL_WORDS = { hire: 'Panel: hire', hold: 'Panel: hold', reject: 'Panel: reject' };
const DECISION_TONE = { approve: 'success', hold: 'warning', reject: 'danger' };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const errText = (e, fallback) => e?.response?.data?.message || fallback;

const muted = { color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' };
const small = { fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' };
const clamp4 = { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

// ── The list of roles ──────────────────────────────────────────────────────

/* The blocks above the roles, and which roles each one shows. */
const ROLE_VIEWS = [
  { key: 'all', label: 'Open roles', hint: 'every vacancy still open', test: () => true },
  { key: 'awaiting', label: 'Awaiting a decision', hint: 'someone is waiting on the Head', test: (r) => r.awaiting > 0, tone: 'warning' },
  { key: 'decided', label: 'Decided', hint: 'approved, on hold or not approved', test: (r) => (r.approved || 0) + (r.onHold || 0) + (r.rejected || 0) > 0 },
  { key: 'none', label: 'No one put forward', hint: 'nobody sent to the Head yet', test: (r) => !r.putForward },
];

export function ShortlistRoles() {
  const navigate = useNavigate();
  const layout = useShellLayout();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [view, setView] = useState('all');
  useEffect(() => {
    api.get('/shortlist-review')
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.status === 403 ? 'You do not have access to shortlists. Ask your administrator.' : 'Could not load the shortlists.'));
  }, []);

  const roles = data ? data.roles : [];
  const counts = useMemo(() => Object.fromEntries(ROLE_VIEWS.map((v) => [v.key, roles.filter(v.test).length])), [roles]);
  const current = ROLE_VIEWS.find((v) => v.key === view) || ROLE_VIEWS[0];
  const shown = roles.filter(current.test);
  const num = (n) => (n ? <span className="num">{n}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>);

  const columns = [
    { key: 'title', label: 'Role', render: (r) => (
      <Link to={`/shortlists/${r.jobId}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 500, color: 'var(--text-1)', textDecoration: 'none' }}>{r.title}</Link>
    ) },
    { key: 'putForward', label: 'Put forward', render: (r) => (r.putForward ? <span className="num" style={{ fontWeight: 600 }}>{r.putForward}</span> : <span style={{ color: 'var(--text-muted)' }}>No one put forward yet</span>) },
    { key: 'approved', label: 'Approved', render: (r) => num(r.approved) },
    { key: 'onHold', label: 'On hold', render: (r) => num(r.onHold) },
    { key: 'rejected', label: 'Not approved', render: (r) => num(r.rejected) },
    { key: 'awaiting', label: 'Status', render: (r) => (r.awaiting > 0
      ? <StatusPill tone="warning">{r.awaiting} awaiting decision</StatusPill>
      : <StatusPill tone="neutral" dot={false}>Nothing waiting</StatusPill>) },
  ];

  return (
    <Page>
      <PageHeader
        title="Shortlists"
        subtitle={data?.canDecide ? 'Candidates HR has put forward to you, by role.' : 'Put candidates forward to the Head, by role.'}
      />
      {err && <Note tone="warning">{err}</Note>}
      {!data && !err && <Card><Loading /></Card>}
      {data && (
        <>
          <StatRow>
            {ROLE_VIEWS.map((v) => (
              <Stat key={v.key} label={v.label} value={counts[v.key]} hint={v.hint}
                tone={counts[v.key] ? v.tone : undefined}
                active={view === v.key} onClick={() => setView(v.key)} />
            ))}
          </StatRow>
          <Section title={current.label} subtitle={`${shown.length} role${shown.length === 1 ? '' : 's'} · open one to compare its candidates`} padding="20px 0 8px">
            {data.roles.length === 0
              ? <EmptyState icon={<Icon name="list-checks" size={36} />} title="No open roles." />
              : shown.length === 0
                ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No roles here.</div>
                : <Table columns={columns} rows={shown} getRowId={(r) => r.jobId} stacked={Boolean(layout?.mobile)}
                    onRowClick={(r) => navigate(`/shortlists/${r.jobId}`)} />}
          </Section>
        </>
      )}
    </Page>
  );
}

// ── One role's shortlist, side by side ─────────────────────────────────────

/* The blocks above the comparison: each narrows the candidates shown. */
const CAND_VIEWS = [
  { key: 'all', label: 'Put forward', hint: 'sent to the Head', test: () => true },
  { key: 'awaiting', label: 'Awaiting decision', hint: 'the Head has not decided', test: (c) => !c.review.decision, tone: 'warning' },
  { key: 'approve', label: 'Approved', hint: 'the Head said yes', test: (c) => c.review.decision === 'approve' },
  { key: 'hold', label: 'On hold', hint: 'kept for later', test: (c) => c.review.decision === 'hold' },
  { key: 'reject', label: 'Not approved', hint: 'the Head said no', test: (c) => c.review.decision === 'reject' },
];

export function ShortlistCompare() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    return api.get(`/shortlist-review/${jobId}`)
      .then((r) => { setData(r.data); setErr(''); })
      .catch((e) => setErr(e?.response?.status === 403 ? 'You do not have access to this shortlist.' : errText(e, 'Could not load the shortlist.')))
      .finally(() => setLoading(false));
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  // Every stage that appears for anyone, in first-seen order, so rows line up.
  const stageRows = useMemo(() => {
    const seen = new Map();
    (data?.candidates || []).forEach((c) => c.stages.forEach((s) => { if (!seen.has(s.stageId)) seen.set(s.stageId, s.label); }));
    return [...seen.entries()].map(([stageId, label]) => ({ stageId, label }));
  }, [data]);

  const all = data?.candidates || [];
  const current = CAND_VIEWS.find((v) => v.key === view) || CAND_VIEWS[0];
  const cands = all.filter(current.test);

  return (
    <Page>
      <PageHeader
        title={data?.role?.title || 'Shortlist'}
        subtitle={data ? `${all.length} put forward · ${data.role.openings} post${data.role.openings === 1 ? '' : 's'}` : undefined}
        actions={(
          <>
            <Button variant="ghost" icon={<Icon name="arrow-left" />} onClick={() => navigate('/pipeline?view=awaiting')}>Back to Pipeline</Button>
            <Button variant="secondary" icon={<Icon name="arrow-clockwise" spin={loading} />} onClick={load} title="Refresh">Refresh</Button>
          </>
        )}
      />

      {err && <Note tone="warning">{err}</Note>}
      {loading && !data && <Card><Loading label="Loading the shortlist" /></Card>}
      {/* The top 3 for this role, worked out from the totals (HIR-HD5) */}
      <Card><TopThree jobId={jobId} /></Card>

      {data && all.length === 0 && (
        <Card><EmptyState icon={<Icon name="users" size={36} />} title="No one put forward yet" description="HR has not put anyone forward for this role yet." /></Card>
      )}

      {data && all.length > 0 && (
        <>
          <StatRow>
            {CAND_VIEWS.map((v) => {
              const n = all.filter(v.test).length;
              return <Stat key={v.key} label={v.label} value={n} hint={v.hint} tone={n ? v.tone : undefined}
                active={view === v.key} onClick={() => setView(v.key)} />;
            })}
          </StatRow>

          <Section title="Side by side" subtitle={`${cands.length} candidate${cands.length === 1 ? '' : 's'}`} padding="20px 0 8px">
            {cands.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No candidates here.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-body-sm)' }}>
                  <thead>
                    <tr>
                      <th style={{ ...headTh, position: 'sticky', left: 0, zIndex: 1, width: 160, minWidth: 160 }} />
                      {cands.map((c) => (
                        <th key={c.id} style={{ ...headTh, minWidth: 240, borderLeft: '1px solid var(--border)' }}>
                          <div style={{ fontWeight: 600, fontSize: 'var(--fs-body-md)', color: 'var(--text-1)' }}>{c.name}</div>
                          <div style={{ ...muted, fontWeight: 400 }}>{c.stage}{c.appliedAt ? ` · applied ${fmtDate(c.appliedAt)}` : ''}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <Row label="CV score">
                      {cands.map((c) => <Cell key={c.id}>{c.cv.unread ? <StatusPill tone="warning">{c.cv.label}</StatusPill> : <Score value={c.cv.score} />}</Cell>)}
                    </Row>
                    <Row label="From the CV">
                      {cands.map((c) => (
                        <Cell key={c.id}>
                          {c.cv.summary && <div style={{ ...small, ...clamp4 }}>{c.cv.summary}</div>}
                          {c.cv.strengths.length > 0 && <div style={{ ...small, color: 'var(--status-success-text)', marginTop: 4 }}>+ {c.cv.strengths.join('; ')}</div>}
                          {c.cv.concerns.length > 0 && <div style={{ ...small, color: 'var(--status-danger-text)', marginTop: 4 }}>− {c.cv.concerns.join('; ')}</div>}
                          {!c.cv.summary && !c.cv.strengths.length && !c.cv.concerns.length && <Dash />}
                        </Cell>
                      ))}
                    </Row>
                    {stageRows.map((s) => (
                      <Row key={s.stageId} label={s.label}>
                        {cands.map((c) => {
                          const r = c.stages.find((x) => x.stageId === s.stageId);
                          if (!r || r.status === 'not_started') return <Cell key={c.id}><Dash text="Not taken" /></Cell>;
                          const card = c.scorecards.find((x) => x.stageId === s.stageId);
                          return (
                            <Cell key={c.id}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Score value={r.score} />
                                {r.passed === true && <StatusPill tone="success">passed ({r.passingScore} needed)</StatusPill>}
                                {r.passed === false && <StatusPill tone="danger">below the {r.passingScore} bar</StatusPill>}
                                {r.passed === null && <StatusPill tone="neutral">{r.status.replace('_', ' ')}</StatusPill>}
                              </div>
                              {card && (
                                <div style={{ ...small, marginTop: 4 }}>
                                  {card.panelDecision && <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{PANEL_WORDS[card.panelDecision]}</div>}
                                  {card.criteria.map((k) => <div key={k.label}>{k.label}: <span className="num">{k.score ?? '—'}/{k.maxMarks}</span></div>)}
                                  {card.panelNotes && <div style={{ fontStyle: 'italic', marginTop: 2 }}>“{card.panelNotes}”</div>}
                                </div>
                              )}
                            </Cell>
                          );
                        })}
                      </Row>
                    ))}
                    <Row label="AI interview">
                      {cands.map((c) => (
                        <Cell key={c.id}>
                          {c.aiInterviews.length === 0 ? <Dash /> : c.aiInterviews.map((a, i) => (
                            <div key={i} style={i ? { marginTop: 8 } : undefined}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Score value={a.score} /><span style={{ fontWeight: 500, color: 'var(--text-1)' }}>{REC_WORDS[a.recommendation] || ''}</span></div>
                              <div style={muted}>{a.label}</div>
                              {a.narrative && <div style={{ ...small, ...clamp4, marginTop: 2 }}>{a.narrative}</div>}
                            </div>
                          ))}
                        </Cell>
                      ))}
                    </Row>
                    <Row label="Put forward">
                      {cands.map((c) => (
                        <Cell key={c.id}>
                          <div style={small}>by {c.review.putForwardBy || '—'}{c.review.putForwardAt ? `, ${fmtDate(c.review.putForwardAt)}` : ''}</div>
                          {c.review.putForwardNote && <div style={{ ...small, fontStyle: 'italic' }}>“{c.review.putForwardNote}”</div>}
                          {data.canPutForward && !c.review.decision && (
                            <Withdraw jobId={jobId} cand={c} onDone={load} />
                          )}
                        </Cell>
                      ))}
                    </Row>
                    <Row label="Head's decision" strong>
                      {cands.map((c) => (
                        <Cell key={c.id}>
                          <Decision jobId={jobId} cand={c} canDecide={data.canDecide} onDone={load} />
                        </Cell>
                      ))}
                    </Row>
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}

      {data?.canPutForward && <PutForward jobId={jobId} eligible={data.eligible} headNotice={data.headNotice} onDone={load} />}
    </Page>
  );
}

/* The comparison is a table turned on its side (a column per candidate), so
   it is drawn here with the design system Table's own header and cell look. */
const headTh = {
  textAlign: 'left', verticalAlign: 'top', padding: '12px 20px', background: 'var(--surface-2)',
  borderBottom: '1px solid var(--border)', fontWeight: 500,
};

function Row({ label, children, strong }) {
  return (
    <tr style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
      <th style={{
        position: 'sticky', left: 0, background: 'var(--surface-1)', textAlign: 'left', padding: '12px 20px',
        fontSize: 'var(--fs-body-sm)', fontWeight: strong ? 600 : 500, color: strong ? 'var(--text-1)' : 'var(--text-muted)',
      }}>{label}</th>
      {children}
    </tr>
  );
}
function Cell({ children }) { return <td style={{ padding: '12px 20px', borderLeft: '1px solid var(--border)', color: 'var(--text-2)' }}>{children}</td>; }
function Dash({ text = '—' }) { return <span style={muted}>{text}</span>; }
function Score({ value }) {
  if (value === null || value === undefined) return <Dash />;
  const color = value >= 75 ? 'var(--status-success-text)' : value >= 60 ? 'var(--text-1)' : 'var(--status-danger-text)';
  return <span className="num" style={{ fontWeight: 600, fontSize: 'var(--fs-body-lg)', color }}>{Math.round(value)}<span style={muted}>%</span></span>;
}

/* The Head's three buttons and a note. Shows the standing decision, who made
   it and when; the Head can change it, and the earlier one stays on record. */
function Decision({ jobId, cand, canDecide, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [changing, setChanging] = useState(false);
  const r = cand.review;

  const send = (decision) => {
    setBusy(decision); setMsg('');
    api.post(`/shortlist-review/${jobId}/candidates/${cand.id}/decision`, { decision, note })
      .then(() => { setNote(''); setChanging(false); onDone(); })
      .catch((e) => setMsg(errText(e, 'Could not record the decision — try again.')))
      .finally(() => setBusy(''));
  };

  const standing = r.decision && (
    <div style={{ display: 'grid', gap: 2, justifyItems: 'start' }}>
      <StatusPill tone={DECISION_TONE[r.decision] || 'neutral'}>{r.decisionWords}</StatusPill>
      <div style={muted}>by {r.decidedBy}{r.decidedAt ? `, ${fmtDate(r.decidedAt)}` : ''}</div>
      {r.decisionNote && <div style={{ ...small, fontStyle: 'italic' }}>“{r.decisionNote}”</div>}
    </div>
  );

  if (!canDecide) return standing || <StatusPill tone="warning">Awaiting the Head's decision</StatusPill>;
  if (r.decision && !changing) {
    return (
      <div style={{ display: 'grid', gap: 6, justifyItems: 'start' }}>
        {standing}
        <Button size="sm" variant="ghost" onClick={() => setChanging(true)}>Change decision</Button>
      </div>
    );
  }
  const btn = (k, variant, icon, label) => (
    <Button size="sm" variant={variant} icon={<Icon name={icon} />} onClick={() => send(k)}
      loading={busy === k} disabled={Boolean(busy)}>{label}</Button>
  );
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {r.decision && standing}
      {/* Notes are capped at 1000 characters, as the server expects. */}
      <Input multiline rows={2} value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} placeholder="Note (optional)" />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {btn('approve', 'primary', 'check-circle', 'Approve')}
        {btn('hold', 'secondary', 'warning-circle', 'Hold')}
        {btn('reject', 'danger', 'x', 'Reject')}
      </div>
      {changing && <div><Button size="sm" variant="ghost" onClick={() => setChanging(false)}>Cancel</Button></div>}
      {msg && <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--status-danger-text)' }}>{msg}</div>}
    </div>
  );
}

function Withdraw({ jobId, cand, onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  return (
    <div style={{ marginTop: 6 }}>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => {
        setBusy(true);
        api.post(`/shortlist-review/${jobId}/candidates/${cand.id}/withdraw`, {})
          .then(onDone)
          .catch((e) => setMsg(errText(e, 'Could not take them off.')))
          .finally(() => setBusy(false));
      }}>
        {busy ? 'Taking off…' : 'Take off the shortlist'}
      </Button>
      {msg && <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--status-danger-text)' }}>{msg}</div>}
    </div>
  );
}


/* HR's side: candidates on this role who could go to the Head. Tick one or
   several and put them forward together: the Head gets one email for the lot
   (once the school has set that email up in Settings → Messages — until then
   this panel says the Head is not being emailed). */
function PutForward({ jobId, eligible, headNotice, onDone }) {
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [told, setTold] = useState(null); // { emailed, message } after an action

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const send = () => {
    const resumeIds = [...picked];
    if (!resumeIds.length) return;
    setBusy(true); setMsg(''); setTold(null);
    api.post(`/shortlist-review/${jobId}/put-forward`, { resumeIds })
      .then((r) => {
        const d = r.data || {};
        const n = (d.putForward || []).length;
        const skipped = (d.skipped || []).length;
        setTold({
          emailed: Boolean(d.headNotice?.emailed),
          message: `${n} put forward${skipped ? ` (${skipped} could not be: ${d.skipped[0].reason})` : ''}. ${d.headNotice?.message || ''}`.trim(),
        });
        setPicked(new Set());
        onDone();
      })
      .catch((e) => setMsg(errText(e, 'Could not put them forward.')))
      .finally(() => setBusy(false));
  };

  return (
    <Section title="Put forward to the Head"
      subtitle="Shortlisted candidates still being assessed. Tick the ones to send; the Head sees them side by side above."
      action={eligible.length > 0 && (
        <Button size="sm" icon={<Icon name="plus" />} onClick={send} loading={busy} disabled={busy || picked.size === 0}>
          Put forward{picked.size ? ` (${picked.size})` : ''}
        </Button>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        {headNotice && !headNotice.ready && (
          <Note tone="warning">{headNotice.reason || 'The Head is not emailed when someone is put forward.'} Tell the Head yourself until then.</Note>
        )}
        {eligible.length === 0 && <div style={{ color: 'var(--text-muted)' }}>No one else on this role is ready to put forward.</div>}
        {eligible.length > 0 && (
          <div>
            {eligible.map((c, i) => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', cursor: 'pointer', color: 'var(--text-1)', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} disabled={busy}
                  style={{ width: 18, height: 18, accentColor: 'var(--brand-primary)' }} />
                <span>{c.name} <span style={muted}>· {c.stage} · {c.cvScore === null ? 'CV could not be read' : `CV ${c.cvScore}%`}</span></span>
              </label>
            ))}
          </div>
        )}
        {told && <Note tone={told.emailed ? 'success' : 'warning'}>{told.message}</Note>}
        {msg && <Note tone="danger">{msg}</Note>}
      </div>
    </Section>
  );
}
